import React from "react";
import { Row, Col } from "react-bootstrap";
import { useFormContext } from "react-hook-form";
import { useSelector } from "react-redux";
import { getFormValues } from "redux-form";

import {
	Button,
	HookFormDatePickerCommon,
	HookFormInputGroup,
	HookFormPhoneInput,
} from "components";
import {
	getCopiedValues,
	getDefaultPrimaryContact,
} from "queries/terminalSolutions/getDefaultValuesTerminalSolution";
import { trimMidSentenceNormalizer, trimNormalizer } from "validators";
import {
	minLength2,
	maxLength30,
	maxLength1000,
	matchesTicketDetails,
	onlyAlphanumericAndSpecialCharacters,
	normalizeAll,
} from "validators/validation";

import styles from "../terminalSolution.module.scss";

type InstallationContactProps = {
	sectionName?: string;
	isLoading?: boolean;
	merchantInfo?: any;
	isHiddenForApplication?: boolean;
	ownerPrincipalFormName?: string;
	handleSaveInProgress?: Function;
};

const InstallationContact = ({
	sectionName = "installation",
	isLoading = false,
	merchantInfo,
	isHiddenForApplication,
	ownerPrincipalFormName = "",
	handleSaveInProgress,
}: InstallationContactProps) => {
	const { register, setValue } = useFormContext();

	const ownerPrincipalFormValues: any = useSelector(state =>
		getFormValues(ownerPrincipalFormName)(state),
	);

	const handleChangePrimaryContact = () => {
		const {
			contactFirstName,
			contactLastName,
			contactPhoneNumber,
			contactEmail,
		} = getDefaultPrimaryContact({
			data: getCopiedValues(ownerPrincipalFormValues, merchantInfo),
		});

		setValue(`${sectionName}.contactFirstName`, contactFirstName, {
			shouldValidate: true,
			shouldDirty: true,
		});
		setValue(`${sectionName}.contactLastName`, contactLastName, {
			shouldValidate: true,
			shouldDirty: true,
		});
		setValue(`${sectionName}.contactPhoneNumber`, contactPhoneNumber, {
			shouldValidate: true,
			shouldDirty: true,
		});
		setValue(`${sectionName}.contactEmail`, contactEmail, {
			shouldValidate: true,
			shouldDirty: true,
		});

		handleSaveInProgress?.();
	};

	return (
		<div data-autotest="installationContactSection">
			<p className={styles.title}>Installation Contact</p>
			<Button
				onClick={handleChangePrimaryContact}
				value="Copy from primary contact"
				variant="secondary"
				className="pl-0 mb-4"
			/>
			<Row>
				<Col md={5}>
					<HookFormInputGroup
						name={`${sectionName}.contactFirstName`}
						register={register}
						label="First Name"
						colInput={6}
						colLabel={6}
						blurLoading={isLoading}
						validator={{
							minLength2,
							maxLength30,
						}}
						normalize={normalizeAll([
							onlyAlphanumericAndSpecialCharacters,
							trimMidSentenceNormalizer,
						])}
						normalizeOnBlur={trimNormalizer}
					/>
				</Col>
				<Col md={{ offset: 2, span: 5 }}>
					<HookFormInputGroup
						name={`${sectionName}.contactLastName`}
						register={register}
						label="Last Name"
						colInput={6}
						colLabel={6}
						blurLoading={isLoading}
						validator={{
							minLength2,
							maxLength30,
						}}
						normalize={normalizeAll([
							onlyAlphanumericAndSpecialCharacters,
							trimMidSentenceNormalizer,
						])}
						normalizeOnBlur={trimNormalizer}
					/>
				</Col>
			</Row>
			<Row>
				<Col md={5}>
					<HookFormPhoneInput
						name={`${sectionName}.contactPhoneNumber`}
						label="Contact Phone"
						isLoading={isLoading}
					/>
				</Col>
				<Col md={{ offset: 2, span: 5 }}>
					<HookFormInputGroup
						name={`${sectionName}.contactEmail`}
						register={register}
						label="Contact Email"
						colInput={6}
						colLabel={6}
						blurLoading={isLoading}
						normalizeOnBlur={trimNormalizer}
					/>
				</Col>
			</Row>
			<Row className="form-group">
				<Col>
					<HookFormInputGroup
						as="textarea"
						name={`${sectionName}.notes`}
						register={register}
						label="Setup / Install Notes"
						colInput={10}
						colLabel={2}
						blurLoading={isLoading}
						labelClassName={styles.labelFullWidth}
						inputBlockClassName={styles.inputFullWidth}
						autoComplete="off"
						validator={{
							maxLength1000,
							matchesTicketDetails,
						}}
					/>
				</Col>
			</Row>
			{!isHiddenForApplication && (
				<>
					<Row>
						<Col md={5}>
							<HookFormDatePickerCommon
								label="Install Date"
								name={`${sectionName}.installAt`}
								isLoading={isLoading}
								maxDate={new Date()}
							/>
						</Col>

						<Col md={{ offset: 2, span: 5 }}>
							<HookFormDatePickerCommon
								name={`${sectionName}.uninstallAt`}
								label="Uninstall Date"
								isLoading={isLoading}
								maxDate={new Date()}
							/>
						</Col>
					</Row>
				</>
			)}
		</div>
	);
};

export default InstallationContact;
